import React from 'react';

const SiteDescription: React.FC = () => {
  return (
    <section className="mt-12 mb-4 space-y-8 p-6 sm:p-8 md:p-10 rounded-3xl shadow-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
      <div className="text-center mb-4">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
          About travelbaba786
        </h2>
      </div>

      <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
        At TravelBaba786, we believe that International boundaries shouldn&apos;t be protected by
        complicated paperwork or expensive middlemen, in our opinion at TravelBaba786. Our mission
        is simple: to offer a recognised one-stop shop for official government E-Visa, ETA, and
        digital entry gateways globally. We developed this tool to cut through the clutter of the
        internet. All too frequently, &quot;copycat&quot; websites that impose unstated service
        fees are encountered by tourists looking for visa information. We solve this by linking you
        directly to the source. By using our curated guides, you ensure your personal data stays on
        official government servers and you pay only the legal application fees. Whether you are a
        solo backpacker or a frequent business traveler, we help you skip the stress and head
        straight to the departure gate.
      </p>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          The Problem: The Hidden Cost of &quot;Official-Looking&quot; Sites
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          When you search for an &quot;e-visa for [Country],&quot; the results page is a
          minefield. The top spots are usually occupied by cleverly disguised commercial agencies
          that spend thousands on ads to mimic government portals.
        </p>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          These sites aren&apos;t just &quot;extra help&quot;—they are often traps. They look
          official, yet they hit you with massive service fees that are sometimes triple the actual
          visa cost. More importantly, handing over your passport scans and credit card details to
          these unofficial middlemen is a massive privacy risk. We&apos;ve seen too many travelers
          lose money or have their applications delayed because they trusted a &quot;look-alike&quot;
          site instead of the actual government authority.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Our Solution: The Verified Directory
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          We created TravelBaba786 to act as the &quot;antidote&quot; to this digital confusion.
          Think of our platform as a human-verified checkmark for the world&apos;s visa portals.
          We&apos;ve done the tedious legwork so you don&apos;t have to—sifting through search
          results to isolate the genuine, government-run links from the expensive imitations.
        </p>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          Our directory provides direct, secure paths to the official E-Visa, ETA, and entry
          application systems for every country on the map. We don&apos;t just provide a link; we
          provide the peace of mind that you are submitting your sensitive passport data exactly
          where it belongs. By cutting out the middlemen, we ensure your travel starts with
          transparency, security, and the lowest possible cost.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          What We Do (and What We Don&apos;t)
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          Transparency is the core of TravelBaba786. It is important to clarify that we are not a
          government agency, nor are we a visa processing service. We do not issue visas, and we
          never handle your personal application data.
        </p>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          Instead, we operate as an independent, traveler-focused directory. Our role is strictly
          to act as a bridge, providing you with direct access to official government websites for
          your own convenience and digital safety. We provide the &quot;where&quot; and the
          &quot;how,&quot; but the &quot;process&quot; happens entirely between you and the
          respective government authorities. By remaining independent, we can provide unbiased
          guidance and ensure you never pay a penny more than the official legal fee.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Why Trust TravelBaba786?
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          In an era where every second travel site claims to be &quot;official,&quot; we stand out
          by actually being honest about where we send you. We aren&apos;t just another list of
          links; we are a resource built by travelers who have been frustrated by the same scams
          you&apos;re trying to avoid.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Global Coverage: Verified Resources for Every Continent
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          Navigating international travel requirements shouldn&apos;t feel like a guessing game.
          TravelBaba786 provides a centralized hub for official entry protocols across all seven
          continents. We manually track the shifting digital landscapes of over 190 countries to
          ensure you find the correct portal on the first try.
        </p>
        <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
          What Our Global Directory Includes:
        </h4>
        <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-700 dark:text-gray-300">
          <li>
            Official E-Visa Portals: Direct access to government servers for countries like India,
            Turkey, and Vietnam—avoiding third-party service fees.
          </li>
          <li>
            ETA Systems (Electronic Travel Authorizations): Verified links for the US (ESTA),
            Canada (eTA), and the upcoming European ETIAS.
          </li>
          <li>
            Digital Arrival Cards: Quick access to mandatory health or customs forms required by
            many Southeast Asian and Caribbean nations.
          </li>
          <li>
            Local Entry Requirements: Links to specific Ministry of Foreign Affairs pages for niche
            destinations where digital systems are not yet standard.
          </li>
        </ul>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          By consolidating these diverse resources into one verified directory, we help travelers
          skip the hours of searching and bypass the &quot;sponsored&quot; commercial traps that
          dominate search engine results.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Verified Information: Our &quot;Official-Only&quot; Promise
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          Accuracy isn&apos;t just a goal at TravelBaba786; it&apos;s our core standard. We
          understand that a broken link or an outdated form can lead to a missed flight or a
          rejected entry. That is why every resource in our directory is sourced exclusively from
          official government domains and verified diplomatic channels.
        </p>
        <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
          How We Verify Our Links:
        </h4>
        <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-700 dark:text-gray-300">
          <li>
            Domain Integrity: We prioritize links ending in official extensions like .gov, .go, or
            .gouv, ensuring you are on a state-authorized server.
          </li>
          <li>
            Manual Audits: Unlike automated scrapers that pull data blindly, we manually check our
            links for &quot;redirect loops&quot; or third-party &quot;look-alike&quot; sites
            that often pop up in search ads.
          </li>
          <li>
            SSL &amp; Security Check: We verify that every portal uses high-level encryption
            (HTTPS) so that your sensitive passport and payment data are never exposed to unsecured
            connections.
          </li>
          <li>
            Cross-Referenced Sources: We double-check our directory against information from the
            International Air Transport Association (IATA) and respective Ministries of Foreign
            Affairs to ensure the requirements listed are current.
          </li>
        </ul>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          By providing a clean, verified path to the source, we eliminate the guesswork and protect
          you from the &quot;processing fees&quot; and data risks associated with unofficial
          middlemen.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Fast &amp; Reliable: Built for the &quot;Last-Minute&quot; Reality
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          Travel plans often move faster than we expect. Whether you are standing in a check-in
          line and just realized you forgot a mandatory entry form, or you&apos;re planning a trip
          for next week, you need answers that are both instant and accurate.
        </p>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          TravelBaba786 is optimized for speed without compromising on safety. We have stripped
          away the &quot;filler&quot; text and complex navigation found on many travel blogs.
          Instead, we offer:
        </p>
        <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-700 dark:text-gray-300">
          <li>
            Instant Redirection: One-click access to the exact page where you start your
            application—no digging through multiple government sub-menus.
          </li>
          <li>
            Mobile-Optimized Links: Our directory is designed to work flawlessly on your
            smartphone, making it the perfect emergency tool for travelers already on the move.
          </li>
          <li>
            Real-Time Reliability: Because we focus solely on official portals, you don&apos;t have
            to worry about an agency&apos;s &quot;slow processing&quot; times. You go straight to
            the government source, which is always the fastest route to approval.
          </li>
        </ul>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          When every minute counts, we provide the shortcut you can actually trust.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Community Driven: Built by Travelers, For Travelers
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          TravelBaba786 wasn&apos;t built in a corporate office; it was born out of the real-world
          challenges we&apos;ve faced while crossing borders. We&apos;ve been that traveler stuck
          at a border gate with a confusing form, and we&apos;ve been the ones who almost paid
          double because of a convincing-looking &quot;copycat&quot; website.
        </p>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          Because we understand the stress of international travel firsthand, we&apos;ve built this
          platform with a community-first mindset:
        </p>
        <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-gray-700 dark:text-gray-300">
          <li>
            Shared Experience: We look at every portal through the eyes of a traveler. If a
            government site is notoriously glitchy or requires a specific browser, we aim to
            highlight those &quot;boots-on-the-ground&quot; details.
          </li>
          <li>
            User-Focused Updates: We listen to our visitors. When the travel community reports a
            new digital nomad visa or a change in an entry requirement, we prioritize those updates
            to keep our directory current.
          </li>
          <li>
            Empowering Independence: Our goal is to give every traveler the confidence to handle
            their own paperwork. We believe that with the right links, anyone can navigate the visa
            process safely and affordably.
          </li>
        </ul>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          We aren&apos;t just a database; we are a resource built by people who love to
          explore—dedicated to making sure your next journey starts with a click, not a headache.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Join Our Community: Help Us Build a Safer Way to Travel
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          If TravelBaba786 has helped you save money or avoid a stressful &quot;middleman&quot;
          trap, we invite you to become part of our mission. We are growing every day, fueled by
          the collective knowledge of globetrotters who believe in transparency and digital safety.
          We encourage you to spread the word by sharing our directory with fellow travelers,
          digital nomad groups, or on your favorite travel forums, as every referral helps one more
          person avoid the reach of copycat website scams.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Help Us Maintain Accuracy
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          We strive for perfection, but the digital landscape of government portals changes
          rapidly. If you encounter a broken link or a redirected page that seems unofficial,
          please alert us at <span className="font-semibold">liaqatapril@gmail.com</span>{' '}
          immediately. Your feedback allows us to perform manual audits and ensures that our global
          community of travelers always has access to the most secure and up-to-date official
          resources.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Disclaimer</h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          TravelBaba786 is a strictly independent information directory and is not affiliated with,
          authorized by, or endorsed by any government agency or official department. We do not
          issue visas, nor do we play any role in the processing of travel applications. Our
          platform is designed solely as a navigational tool to provide you with direct access to
          official government portals for your own convenience and digital safety. While we make
          every effort to ensure the accuracy of our links, all final application decisions and
          data privacy matters remain between the traveler and the respective government authority.
          We do not collect or store your personal passport information, and we recommend that
          users always verify the latest entry requirements directly on official state websites
          before making travel arrangements.
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Business and Partnership Inquiries
        </h3>
        <p className="text-base md:text-lg leading-relaxed text-gray-700 dark:text-gray-300">
          We are always open to collaborating with organizations that share our commitment to safe
          and transparent international travel. If you represent a travel platform, a diplomatic
          service, or a technology provider and would like to discuss professional partnerships,
          media inquiries, or business opportunities, please reach out to us at{' '}
          <span className="font-semibold">liaqatapril@gmail.com</span>. Our team reviews every
          proposal to ensure that any potential collaboration aligns with our mission of providing
          travelers with direct and secure access to official government resources.
        </p>
      </div>
    </section>
  );
};

export default SiteDescription;

